import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Heart, User, CheckCircle } from 'lucide-react'
import { db } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import PlantPreview from '../components/PlantPreview'
import { useI18n } from '../i18n/I18nProvider'

function Community() {
  const [plants, setPlants] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSuccessNotif, setShowSuccessNotif] = useState(false)
  const user = useAuthStore((state) => state.user)
  const location = useLocation()
  const { t } = useI18n()

  useEffect(() => {
    loadCommunityPlants()
    
    // Check if redirected from publish
    if (location.state?.publishSuccess) {
      setShowSuccessNotif(true)
      // Clear the state
      window.history.replaceState({}, document.title)
      // Hide notification after 5 seconds
      setTimeout(() => setShowSuccessNotif(false), 5000)
    }
  }, [])

  const loadCommunityPlants = async () => {
    try {
      setLoading(true)
      const data = await db.getCommunityPlants(50, 0)
      setPlants(data || [])
    } catch (error) {
      console.error('Failed to load community plants:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLike = async (plantId) => {
    if (!user) return
    
    try {
      await db.likePlant(plantId, user.id)
      // Reload plants to update like count
      await loadCommunityPlants()
    } catch (error) {
      console.error('Failed to like plant:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Success Notification */}
        {showSuccessNotif && (
          <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in-right">
            <CheckCircle size={24} />
            <div>
              <p className="font-bold">{t('community.successTitle')}</p>
              <p className="text-sm">{t('community.successDescription')}</p>
            </div>
          </div>
        )}
        
        <h1 className="text-4xl font-bold text-green-800 mb-4 text-center">
          {t('community.title')}
        </h1>
        <p className="text-gray-600 text-center mb-12">
          {t('community.subtitle')}
        </p>

        {plants.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg mb-4">
              {t('community.emptyTitle')}
            </p>
            <p className="text-gray-500">
              {t('community.emptySubtitle')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plants.map((plant) => (
              <div
                key={plant.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow"
              >
                {/* Plant Preview */}
                <div className="bg-gradient-to-b from-sky-300 to-green-100 aspect-square flex items-center justify-center overflow-hidden">
                  {plant.plant_data?.snapshot ? (
                    <img
                      src={plant.plant_data.snapshot}
                      alt={t('community.cardPreviewAlt', 'Shared garden preview')}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <PlantPreview plantData={plant} size={300} />
                  )}
                </div>

                {/* Plant Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User size={20} className="text-gray-600" />
                      <span className="font-medium text-gray-800">
                        {plant.created_by || t('community.cardOwnerUnknown')}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => handleLike(plant.id)}
                      className={`flex items-center gap-1 transition ${
                        user ? 'hover:text-red-500 cursor-pointer' : 'cursor-not-allowed opacity-50'
                      }`}
                      disabled={!user}
                      title={user ? t('community.likeButtonLoggedIn') : t('community.likeButtonLoggedOut')}
                    >
                      <Heart size={20} className="fill-current text-red-500" />
                      <span className="font-bold">{plant.likes || 0}</span>
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>
                      {(() => {
                        const data = plant.plant_data
                        const count = Array.isArray(data?.plants) ? data.plants.length : 1
                        const label = count > 1 ? t('community.cardPlantPlural') : t('community.cardPlantSingular')
                        return `${count} ${label}`
                      })()}
                    </span>
                    <span>
                      {plant.created_at ? 
                        new Date(plant.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })
                        : 'Date inconnue'
                      }
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default Community
